import React, { useState, useRef } from 'react';
import { MapPin, Phone, Mail, ArrowUpRight, Upload, FileText, X, CheckCircle2, Loader2 } from 'lucide-react';
import { company } from '../../mock';
import { useContent } from '../../content';
import { useLang } from '../../i18n';

const FieldLabel = ({ children }) => (
  <label className="label-mono text-white/45 block mb-2">{children}</label>
);

const inputCls =
  'w-full bg-transparent border-b border-white/15 focus:border-ember text-white font-body text-[15px] py-2.5 outline-none transition-colors placeholder:text-white/25';

const Contact = () => {
  const { contactInfo, services } = useContent();
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service_interest: '', message: '' });
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (list) => {
    const arr = Array.from(list);
    setFiles((prev) => [...prev, ...arr]);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    const MAX_TOTAL_UPLOAD = 25 * 1024 * 1024; // 25 MB
    const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
    if (totalSize > MAX_TOTAL_UPLOAD) {
      setStatus('error');
      setErrorMsg(t('contact.filesTooLarge'));
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append('files', f));

      const res = await fetch(`${BACKEND_URL}/api/contact`, { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        let msg = t('contact.errorGeneric');
        if (typeof data.detail === 'string') {
          msg = data.detail;
        } else if (Array.isArray(data.detail) && data.detail.length) {
          msg = data.detail.map((d) => d.msg || '').filter(Boolean).join(' · ') || msg;
        }
        throw new Error(msg);
      }
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', service_interest: '', message: '' });
      setFiles([]);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Unable to send inquiry.');
    }
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-12 lg:gap-16 items-start">
      {/* Left info */}
      <div>
        <div className="label-mono text-ember mb-6">[{contactInfo.index}] · {contactInfo.label}</div>
        <h2 className="font-display font-light text-white text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
          {contactInfo.heading}
        </h2>
        <p className="mt-6 text-white/70 font-body text-base leading-relaxed max-w-md">{contactInfo.sub}</p>

        <div className="mt-12 space-y-8">
          <div className="border-t border-white/10 pt-6 flex gap-4">
            <MapPin size={18} className="text-ember shrink-0 mt-1" strokeWidth={1.6} />
            <div>
              <div className="label-mono text-white/45 mb-2">{t('footer.hq')}</div>
              <div className="text-white/85 font-body">{company.address[0]}</div>
              <div className="text-white/85 font-body">{company.address[1]}, {company.address[2]}</div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex gap-4">
            <Phone size={18} className="text-ember shrink-0 mt-1" strokeWidth={1.6} />
            <div>
              <div className="label-mono text-white/45 mb-2">{t('footer.phone')}</div>
              <a href={`tel:${company.phone.replace(/\s/g, '')}`} className="text-white/85 font-body hover:text-ember transition-colors">{company.phone}</a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex gap-4">
            <Mail size={18} className="text-ember shrink-0 mt-1" strokeWidth={1.6} />
            <div>
              <div className="label-mono text-white/45 mb-2">{t('footer.emailLabel')}</div>
              {company.emails.map((em) => (
                <a key={em} href={`mailto:${em}`} className="block text-white/85 font-body hover:text-ember transition-colors">{em}</a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="glass-card p-8 md:p-10" style={{ transition: 'none', transform: 'none' }}>
        {status === 'success' ? (
          <div className="flex flex-col items-center justify-center text-center py-16" data-testid="contact-success">
            <CheckCircle2 size={48} className="text-ember" strokeWidth={1.4} />
            <h3 className="mt-6 font-display font-medium text-white text-2xl">{t('contact.successTitle')}</h3>
            <p className="mt-3 text-white/60 font-body max-w-sm">{t('contact.successBody')}</p>
            <button onClick={() => setStatus('idle')} className="mt-8 label-mono text-ember hover:text-ember-hover">{t('contact.another')}</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form">
            <div className="label-mono text-white/45 mb-8">{contactInfo.formLabel}</div>

            {/* Honeypot */}
            <input type="text" name="website" tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <FieldLabel>{t('contact.name')}</FieldLabel>
                <input data-testid="input-name" value={form.name} onChange={set('name')} className={inputCls} />
              </div>
              <div>
                <FieldLabel>{t('contact.company')}</FieldLabel>
                <input data-testid="input-company" value={form.company} onChange={set('company')} className={inputCls} />
              </div>
              <div>
                <FieldLabel>{t('contact.email')}</FieldLabel>
                <input data-testid="input-email" type="email" required value={form.email} onChange={set('email')} className={inputCls} />
              </div>
              <div>
                <FieldLabel>{t('contact.phone')}</FieldLabel>
                <input data-testid="input-phone" value={form.phone} onChange={set('phone')} className={inputCls} />
              </div>
            </div>

            <div className="mt-6">
              <FieldLabel>{t('contact.service')}</FieldLabel>
              <select data-testid="input-service" value={form.service_interest} onChange={set('service_interest')} className={`${inputCls} contact-select cursor-pointer`}>
                <option value="" className="bg-navy">{t('contact.selectService')}</option>
                {services.items.map((s) => (
                  <option key={s.no} value={s.title} className="bg-navy">{s.title}</option>
                ))}
              </select>
            </div>

            <div className="mt-6">
              <FieldLabel>{t('contact.message')}</FieldLabel>
              <textarea
                data-testid="input-message"
                required
                rows={4}
                value={form.message}
                onChange={set('message')}
                placeholder={t('contact.messagePlaceholder')}
                className={`${inputCls} resize-none`}
              />
            </div>

            {/* Attachments */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <FieldLabel>{t('contact.attachments')}</FieldLabel>
                <span className="label-mono text-white/35">{t('contact.attachHint')}</span>
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                className={`cursor-pointer border border-dashed px-6 py-8 text-center transition-colors ${dragOver ? 'border-ember bg-ember/5' : 'border-white/20 hover:border-white/40'}`}
              >
                <Upload size={20} className="mx-auto text-ember/80 mb-3" strokeWidth={1.6} />
                <p className="text-white/70 font-body text-sm">
                  <span className="text-ember">{t('contact.choose')}</span> {t('contact.orDrop')}
                </p>
                <p className="mt-1.5 label-mono text-white/30">{t('contact.fileTypes')}</p>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="input-files" />
              </div>

              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between border border-white/10 bg-white/[0.02] px-3 py-2">
                      <span className="flex items-center gap-2 text-white/75 font-body text-sm truncate">
                        <FileText size={14} className="text-ember shrink-0" />
                        <span className="truncate">{f.name}</span>
                      </span>
                      <button type="button" onClick={() => removeFile(i)} className="text-white/40 hover:text-ember"><X size={15} /></button>
                    </div>
                  ))}
                  {(() => {
                    const totalMB = files.reduce((s, f) => s + (f.size || 0), 0) / (1024 * 1024);
                    const over = totalMB > 25;
                    return (
                      <div data-testid="upload-total" className={`label-mono text-right ${over ? 'text-red-400' : 'text-white/40'}`}>
                        {totalMB.toFixed(1)} / 25 MB{over ? ' · too large' : ''}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {status === 'error' && (
              <p className="mt-5 text-red-300 font-body text-sm">{errorMsg}</p>
            )}

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={status === 'sending'}
              className="group mt-8 inline-flex items-center gap-3 px-8 py-4 label-mono text-white rounded-full shadow-lg shadow-ember/25 hover:shadow-ember/40 hover:brightness-105 transition-all disabled:opacity-70"
              style={{ background: 'linear-gradient(90deg, #f7a24a 0%, #ef7b24 100%)' }}
            >
              {status === 'sending' ? (<><Loader2 size={16} className="animate-spin" /> {t('contact.sending')}</>) : (<>{t('contact.send')} <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Contact;
